import { X } from "lucide-react";
import { formatCurrency } from "@vaulzo/core/constants";
import { getFundNav } from "@vaulzo/core/api/market";
import FundSelect from "./FundSelect";

const underline =
  "border-0 border-b-[1.5px] border-vaulzo-input-line bg-transparent py-2 text-vaulzo-ink outline-none transition-colors placeholder:text-vaulzo-subtle focus:border-vaulzo-accent";

/**
 * Row-based editor for a mutual-fund portfolio: fund search + units +
 * optional average buy NAV per row, with live values from the backend's
 * NAV cache. The server does the authoritative pricing on save.
 */
export default function MFHoldingsEditor({ holdings, setHoldings }) {
  const patchRow = (i, patch) =>
    setHoldings((rows) => rows.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  const removeRow = (i) => setHoldings((rows) => rows.filter((_, idx) => idx !== i));
  const addRow = () =>
    setHoldings((rows) => [
      ...rows,
      { schemeCode: "", schemeName: "", units: "", avgNav: "", lastNav: null, navDate: "" },
    ]);

  // Selecting a fund also pulls its NAV so the row can preview a value.
  const selectFund = async (i, fund) => {
    patchRow(i, { ...fund, lastNav: null, navDate: "" });
    try {
      const doc = await getFundNav(fund.schemeCode);
      patchRow(i, { lastNav: parseFloat(doc.nav) || null, navDate: doc.nav_date || "" });
    } catch {
      /* preview only — the server prices authoritatively on save */
    }
  };

  let total = 0;
  let invested = 0;
  let investedNow = 0;
  holdings.forEach((h) => {
    const units = parseFloat(h.units);
    if (!(units > 0) || !(h.lastNav > 0)) return;
    total += units * h.lastNav;
    const avg = parseFloat(h.avgNav);
    if (avg > 0) {
      invested += units * avg;
      investedNow += units * h.lastNav;
    }
  });
  const gain = invested > 0 ? ((investedNow - invested) / invested) * 100 : null;

  return (
    <div className="block">
      <div className="mb-3 flex items-baseline justify-between">
        <span className="text-[13px] font-semibold text-vaulzo-muted">Funds</span>
        <span className="text-[12px] text-vaulzo-fainter">Avg. NAV is optional — shows your gain</span>
      </div>

      {holdings.map((h, i) => {
        const units = parseFloat(h.units);
        const rowValue = units > 0 && h.lastNav > 0 ? units * h.lastNav : null;
        const avg = parseFloat(h.avgNav);
        const rowGain = rowValue && avg > 0 ? ((h.lastNav - avg) / avg) * 100 : null;

        return (
          <div key={i} className="border-b border-vaulzo-row py-3 first:pt-1 last:border-b-0">
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <FundSelect value={h.schemeName} onSelect={(fund) => selectFund(i, fund)} />
              </div>
              <button
                type="button"
                onClick={() => removeRow(i)}
                disabled={holdings.length === 1}
                aria-label="Remove fund"
                className="flex w-5 flex-none justify-center text-vaulzo-bar transition-colors hover:text-vaulzo-danger disabled:opacity-30"
              >
                <X className="h-4 w-4" strokeWidth={1.7} />
              </button>
            </div>

            <div className="mt-2 flex gap-4">
              <label className="flex-1">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
                  Units
                </span>
                <input
                  type="number"
                  step="any"
                  min="0"
                  inputMode="decimal"
                  required
                  value={h.units}
                  onChange={(e) => patchRow(i, { units: e.target.value })}
                  placeholder="7526.588"
                  className={`num w-full text-[15px] font-semibold ${underline}`}
                />
              </label>
              <label className="w-[130px]">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
                  Avg. NAV (₹)
                </span>
                <input
                  type="number"
                  step="any"
                  min="0"
                  inputMode="decimal"
                  value={h.avgNav}
                  onChange={(e) => patchRow(i, { avgNav: e.target.value })}
                  placeholder="36.1367"
                  className={`num w-full text-[15px] ${underline}`}
                />
              </label>
            </div>

            {rowValue && (
              <div className="num pt-2 text-[12px] text-vaulzo-fainter">
                ≈ {formatCurrency(rowValue)} at NAV {h.lastNav}
                {h.navDate && ` (${h.navDate})`}
                {rowGain != null && (
                  <span className={rowGain >= 0 ? "text-vaulzo-accent" : "text-vaulzo-danger"}>
                    {" "}
                    · {rowGain >= 0 ? "↑" : "↓"} {Math.abs(rowGain).toFixed(2)}%
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={addRow}
        className="mt-3 text-[13px] font-semibold text-vaulzo-accent transition-colors hover:text-vaulzo-accent-hover"
      >
        + Add fund
      </button>

      {total > 0 && (
        <div className="num mt-4 rounded-xl bg-vaulzo-row/60 px-4 py-3 text-[13px] text-vaulzo-muted">
          Total <span className="font-semibold text-vaulzo-ink">{formatCurrency(total)}</span>
          {gain != null && (
            <span className={`font-semibold ${gain >= 0 ? "text-vaulzo-accent" : "text-vaulzo-danger"}`}>
              {" "}
              · {gain >= 0 ? "↑" : "↓"} {Math.abs(gain).toFixed(2)}%
            </span>
          )}
        </div>
      )}
    </div>
  );
}
